import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { Divider } from 'primereact/divider';
import ConditionBuilder from './ConditionBuilder';
import { validateRuleForm } from '../utils/ruleValidation';

/**
 * RuleDialog Component
 *
 * Modal form for creating and editing rules with conditions
 *
 * @component
 */
const RuleDialog = ({ 
  visible, 
  isEditMode, 
  rule = null, 
  onHide, 
  onSave, 
  loading = false,
  teams: teamsProp = null
}) => {
  const fileInputRef = React.useRef(null);

  // Use prop teams if provided (from RulesManagement direct API), fall back to Redux
  const teamsFromRedux = useSelector(state => state.dataCategorization.teams);
  const teams = teamsProp || teamsFromRedux;
  const allRules = useSelector(state => state.dataCategorization.rules);
  const categories = useSelector(state => state.dataCategorization.categories) || [];

  // Initialize form state with lazy initialization to access teams
  const getInitialFormState = () => ({
    name: '',
    team: teams.length > 0 ? teams[0].value : '',
    targetCategory: '',
    enabled: true,
    logicalOperator: 'AND',
    conditions: [
      {
        field: '',
        operator: 'CONTAINS',
        value: '',
        caseSensitive: false,
        logicalOperator: 'AND'
      }
    ]
  });

  const [formData, setFormData] = useState(() => getInitialFormState());
  const [errors, setErrors] = useState({});
  const [excelHeaders, setExcelHeaders] = useState([]);
  // Handle Excel file upload and extract headers
  const handleExcelUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const data = new Uint8Array(evt.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const headers = XLSX.utils.sheet_to_json(firstSheet, { header: 1 })[0] || [];
      setExcelHeaders(headers.filter(Boolean));
    };
    reader.readAsArrayBuffer(file);
  };

  // Convert teams to dropdown options
  const teamOptions = teams.map(team => ({
    label: team.label,
    value: team.value
  }));

  // Populate form when editing
  useEffect(() => {
    if (isEditMode && rule) {
      setFormData({
        name: rule.name || '',
        team: rule.team || (teams.length > 0 ? teams[0].value : ''),
        targetCategory: rule.targetCategory || '',
        enabled: rule.enabled === undefined ? true : rule.enabled,
        logicalOperator: rule.logicalOperator || 'AND',
        conditions: rule.conditions?.length > 0
          ? rule.conditions
          : getInitialFormState().conditions
      });
    } else {
      setFormData(getInitialFormState());
    }
    setErrors({});
  }, [isEditMode, rule, visible, teams]);

  /**
   * Handle field change
   */
  const handleFieldChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  /**
   * Handle conditions change
   */
  const handleConditionsChange = (conditions) => {
    setFormData(prev => ({
      ...prev,
      conditions
    }));
    // Clear condition errors
    setErrors(prev => {
      const newErrors = { ...prev };
      Object.keys(newErrors).forEach(key => {
        if (key.startsWith('condition_')) {
          delete newErrors[key];
        }
      });
      if (newErrors.conditions) {
        delete newErrors.conditions;
      }
      return newErrors;
    });
  };

  /**
   * Handle save
   */
  const handleSave = () => {
    const validation = validateRuleForm(formData);

    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    // Check for duplicate rule names in the same team
    const rulesInTeam = allRules.filter(r => r.team === formData.team);
    const isDuplicate = rulesInTeam.some(r => 
      r.name?.toLowerCase() === formData.name?.toLowerCase() && 
      (!isEditMode || r.ruleId !== rule?.ruleId)
    );

    if (isDuplicate) {
      setErrors({
        ...validation.errors,
        name: 'Rule name must be unique within this team. A rule with this name already exists.'
      });
      return;
    }

    // Conditions are already flattened by ConditionBuilder's groupsToFlat
    // Ensure categoryId is included for the backend
    const payload = { ...formData };
    if (!payload.categoryId && payload.targetCategory) {
      const match = categories.find(c => c.name === payload.targetCategory);
      if (match) payload.categoryId = match.id;
    }
    onSave(payload);
  };

  /**
   * Handle cancel
   */
  const handleCancel = () => {
    setFormData(getInitialFormState());
    setErrors({});
    onHide();
  };

  /**
   * Dialog footer
   */
  const dialogFooter = (
    <div>
      <Button
        label="Cancel"
        icon="pi pi-times"
        className="p-button-text"
        onClick={handleCancel}
        disabled={loading}
      />
      <Button
        label={isEditMode ? 'Update' : 'Create'}
        icon="pi pi-check"
        onClick={handleSave}
        loading={loading}
      />
    </div>
  );

  return (
    <Dialog
      header={isEditMode ? 'Edit Rule' : 'Create New Rule'}
      visible={visible}
      onHide={handleCancel}
      style={{ width: '900px', maxWidth: '95vw' }}
      footer={dialogFooter}
      className="rule-dialog"
      modal
      draggable={false}
    >
      <div className="rule-form">
        <div className="form-grid">
          <div className="p-field">
            <label htmlFor="name">Rule Name *</label>
            <InputText
              id="name"
              value={formData.name}
              onChange={(e) => handleFieldChange('name', e.target.value)}
              className={errors.name ? 'p-invalid' : ''}
              placeholder="e.g., Hardware Damage - Bizerba"
            />
            {errors.name && <small className="p-error">{errors.name}</small>}
          </div>

          <div className="p-field">
            <label htmlFor="team">Team *</label>
            <Dropdown
              id="team"
              value={formData.team}
              options={teamOptions}
              onChange={(e) => handleFieldChange('team', e.value)}
              placeholder="Select Team"
              className={errors.team ? 'p-invalid' : ''}
            />
            {errors.team && <small className="p-error">{errors.team}</small>}
          </div>
        </div>

        <div className="p-field">
          <label htmlFor="targetCategory">Target Category *</label>
          <InputText
            id="targetCategory"
            value={formData.targetCategory}
            onChange={(e) => handleFieldChange('targetCategory', e.target.value)}
            className={errors.targetCategory ? 'p-invalid' : ''}
            placeholder="e.g., Food Service Scale (Bizerba/Hobart)"
          />
          {errors.targetCategory && <small className="p-error">{errors.targetCategory}</small>}
        </div>

        <div className="form-grid">
          <div className="p-field-checkbox">
            <Checkbox
              inputId="enabled"
              checked={formData.enabled}
              onChange={(e) => handleFieldChange('enabled', e.checked)}
            />
            <label htmlFor="enabled">Enable this rule immediately</label>
          </div>
        </div>

        <Divider />

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Button 
              icon="pi pi-upload" 
              className="p-button-sm p-button-text" 
              label="Upload Excel Headers" 
              type="button" 
              onClick={() => fileInputRef.current && fileInputRef.current.click()} 
            />
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              style={{ display: 'none' }}
              onChange={handleExcelUpload}
            />
          </div>
        </div>

        <ConditionBuilder
          conditions={formData.conditions}
          onConditionsChange={handleConditionsChange}
          errors={errors}
          excelHeaders={excelHeaders}
          isEditMode={isEditMode}
        />
      </div>
    </Dialog>
  );
};

RuleDialog.propTypes = {
  visible: PropTypes.bool.isRequired,
  isEditMode: PropTypes.bool.isRequired,
  rule: PropTypes.shape({
    name: PropTypes.string,
    team: PropTypes.string,
    targetCategory: PropTypes.string,
    enabled: PropTypes.bool,
    logicalOperator: PropTypes.string,
    conditions: PropTypes.arrayOf(PropTypes.object)
  }),
  onHide: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  teams: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string
  }))
};

export default RuleDialog;

